import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api.js';
import { DailyCashCountModal } from './DailyCashCountModal.jsx';

// ─── Daily cash counts (weekly grid) ───────────────────────────────────────
// Shows one row per location per day of the selected week. Each cell is
// draft / pending / certified. Manager clicks a cell to open an inline
// modal for certify or backfill — never leaves Monarch.
export function DailyCashCountsPanel({ weekStart, weekEnd, currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState([]);
  const [locs, setLocs] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');
  const [modal, setModal] = useState(null); // { locationId, locationName, businessDate }
  const [reloadTick, setReloadTick] = useState(0);

  // Build the seven business dates in the selected week (Sun..Sat).
  const days = useMemo(() => {
    const out = [];
    const start = new Date(weekStart + 'T12:00:00Z');
    for (let i = 0; i < 7; i++) {
      const d = new Date(start); d.setUTCDate(start.getUTCDate() + i);
      out.push(d.toISOString().slice(0, 10));
    }
    return out;
  }, [weekStart]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true); setErr('');
      try {
        const ls = await api('GET', '/api/locations', null, headers);
        if (!alive) return;
        setLocs(ls || []);
        // Pull each location's counts for this week in parallel.
        const per = await Promise.all(ls.map(async (l) => {
          try {
            const r = await api('GET',
              `/api/cash-count/daily/range?location_id=${l.id}&start=${weekStart}&end=${weekEnd}`,
              null, headers);
            return { locId: l.id, counts: r.counts || [] };
          } catch { return { locId: l.id, counts: [] }; }
        }));
        if (!alive) return;
        // Reshape into { location_id: { business_date: row } }
        const byLoc = new Map();
        per.forEach(({ locId, counts }) => {
          const m = new Map();
          counts.forEach(c => m.set(String(c.business_date).slice(0, 10), c));
          byLoc.set(locId, m);
        });
        setRows(ls.map(l => ({
          location: l,
          byDay: byLoc.get(l.id) || new Map(),
        })));
      } catch (e) { setErr(e.message || String(e)); }
      finally { if (alive) setBusy(false); }
    })();
    return () => { alive = false; };
  }, [weekStart, weekEnd, reloadTick]);

  // Business dates are ET. Days that haven't happened yet aren't "Missing" —
  // the current in-progress week is watchable here (CJ 2026-07-09), so a
  // Wednesday view of this week must not flag Thu–Sun.
  const todayEt = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
  function statusOf(row, day) {
    if (row) return row.certified_at ? 'certified' : 'pending';
    if (day > todayEt) return 'upcoming';
    if (day === todayEt) return 'due';
    return 'missing';
  }
  function pillFor(s) {
    if (s === 'certified') return { bg: '#e6f2ec', fg: '#226b4a', label: 'Certified' };
    if (s === 'pending')   return { bg: '#fbf7ec', fg: '#8a6b1a', label: 'Pending' };
    if (s === 'due')       return { bg: '#eef4fb', fg: '#3a6ea5', label: 'Due tonight' };
    if (s === 'upcoming')  return { bg: '#fafafa', fg: '#bdbdbd', label: 'Upcoming' };
    return { bg: '#fdecea', fg: '#b3261e', label: 'Missing' };
  }
  function totalStr(row, status) {
    if (!row) return '';
    if (status === 'certified') return '$' + (Number(row.final_drawer_total) || 0).toFixed(2);
    if (status === 'pending')   return '$' + (Number(row.staff_drawer_total) || 0).toFixed(2);
    return '';
  }
  function dayLabel(ymd) {
    const d = new Date(ymd + 'T12:00:00Z');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: 'UTC' });
  }
  function openReview(locId, locName, day) {
    setModal({ locationId: locId, locationName: locName, businessDate: day });
  }

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div>
          <div className="text-base font-semibold">Daily cash counts</div>
          <div className="text-xs text-gray-500">
            Closing barista submissions per day + location. Click a cell to review or backfill.
          </div>
        </div>
      </div>
      {busy && <div className="text-xs text-gray-500">Loading…</div>}
      {err && <div className="text-xs" style={{ color: '#c62828' }}>{err}</div>}
      {!busy && (
        <div style={{ overflowX: 'auto', marginTop: 6 }}>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', color: '#757575' }}>
                <th style={{ padding: 6 }}>Location</th>
                {days.map(d => (
                  <th key={d} style={{ padding: 6, textAlign: 'center' }}>{dayLabel(d)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ location, byDay }) => (
                <tr key={location.id} style={{ borderBottom: '1px solid #fafafa' }}>
                  <td style={{ padding: 6, fontWeight: 600 }}>{location.name}</td>
                  {days.map(d => {
                    const row = byDay.get(d);
                    const s = statusOf(row, d);
                    const p = pillFor(s);
                    const future = s === 'upcoming'; // nothing to review or backfill yet
                    return (
                      <td key={d} style={{ padding: 4, textAlign: 'center' }}>
                        <button onClick={() => !future && openReview(location.id, location.name, d)}
                          disabled={future}
                          title={future ? `${d} hasn't happened yet` : `Open ${location.name} · ${d}`}
                          style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center',
                                    gap: 2, padding: '6px 8px', border: '1px solid #eee', borderRadius: 6,
                                    background: '#fff', cursor: future ? 'default' : 'pointer', width: '100%',
                                    opacity: future ? 0.6 : 1 }}>
                          <span style={{ background: p.bg, color: p.fg,
                                          fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
                                          textTransform: 'uppercase', padding: '1px 6px', borderRadius: 999 }}>
                            {p.label}
                          </span>
                          <span style={{ fontVariantNumeric: 'tabular-nums', color: '#212121' }}>
                            {totalStr(row, s) || '—'}
                          </span>
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modal && (
        <DailyCashCountModal
          locationId={modal.locationId}
          locationName={modal.locationName}
          businessDate={modal.businessDate}
          currentUser={currentUser}
          onClose={() => setModal(null)}
          onSaved={() => setReloadTick(t => t + 1)}
        />
      )}
    </div>
  );
}
